import { Account, prettifyAttoAlphAmount, prettifyTokenAmount, web3 } from "@alephium/web3";
import { useWallet } from "@alephium/web3-react";
import { Event } from "@alephium/web3/dist/src/api/api-explorer";
import { useEffect, useState } from "react";
import { Leaderboard, LeaderboardTypes, RocketRange } from "src/artifacts/ts";
import { LeaderboardEntry, RocketRangeUserBet } from "src/artifacts/ts/types";
import { formatAddress, formatTimeAgo, formatTxId } from "src/components/utils/Formatters";
import { getContractConfig } from "src/services/utils";

export default function RocketRangeMyBets() {
    web3.setCurrentNodeProvider(process.env.REACT_APP_NODE_URL ? process.env.REACT_APP_NODE_URL : "https://wallet.mainnet.alephium.org");
    const { connectionStatus, nodeProvider, account, signer } = useWallet()

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userBets, setUserBets] = useState<any[]>([] as any[]);

    const fetchData = async (address: string | undefined) => {
        try {
            if (address) {
                const betsRaw = await fetch(`${process.env.REACT_APP_EXPLORER_BACKEND_URL}/contract-events/contract-address/${getContractConfig("RocketRange").address}/input-address/${address}?event_index=6&page=1&limit=100`)
                const betsJson = await betsRaw.json();
                const payoutsRaw = await fetch(`${process.env.REACT_APP_EXPLORER_BACKEND_URL}/contract-events/contract-address/${getContractConfig("RocketRange").address}/input-address/${address}?event_index=8&page=1&limit=100`)
                let payoutsJson = await payoutsRaw.json();
                if (betsJson && betsJson.length) {
                    if (!payoutsJson) payoutsJson = [] as any[];
                    let aggregated = [...(filterUnique(payoutsJson as any[])), ...(filterUnique(betsJson as any[]))]
                        .sort((a, b) => parseInt(b.fields ? b.fields[1].value + "" : "0") - parseInt(a.fields ? a.fields[1].value + "" : "0"));
                    setUserBets(aggregated);
                    setError("");
                } else {
                    setError("No Predictions yet.")
                }
            } else {
                setError("Wallet not connected.")
            }
        } catch (e) {
            setError("Error loading data.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    const filterUnique = (array: any[]) => {
        // Filter unique objects based on their hash
        const uniqueObjects = [] as any[];
        const seenHashes = new Set();

        for (const obj of array) {
            const hash = JSON.stringify(obj);
            if (!seenHashes.has(hash)) {
                seenHashes.add(hash);
                uniqueObjects.push(obj); // Add to unique list only if hash not seen
            }
        }

        return uniqueObjects;
    }

    useEffect(() => {
        setLoading(true);
        fetchData(account?.address);
    }, [account]);

    const resultMap = {
        1: "Down 4%+",
        2: "Down 1-4%",
        3: "No Change",
        4: "Up 1-4%",
        5: "Up 4%+",
    };

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold text-white text-center mb-4">My Predictions</h2>

            {loading ? (
                <p className="text-gray-400">Loading...</p>
            ) :
                error ? (
                    <p className="text-red-400">{error}</p>
                ) : (
                    <>
                        <div>
                            {userBets.length === 0 ? (
                                <p className="text-gray-400">No past predictions found.</p>
                            ) : (
                                <ul className="space-y-4">
                                    {userBets.map((bet, index) => {
                                        if (bet.fields) {
                                            const transactionId = bet.txHash;
                                            const timeOfAction = formatTimeAgo(bet.timestamp); // Assuming `bet.timestamp` is in seconds

                                            if (bet.eventIndex == 6) {
                                                return (
                                                    <li key={index} className="bg-gray-800 p-4 rounded-lg shadow-md flex justify-between items-center">
                                                        <div>
                                                            <p className="text-gray-300">
                                                                Round: <strong>{bet.fields[1].value + ""}</strong>
                                                            </p>
                                                            <p className="text-gray-300">
                                                                Amount: <strong>{prettifyAttoAlphAmount(BigInt(bet.fields[2].value + ""))} ALPH</strong>
                                                            </p>
                                                            <p className="text-gray-300">
                                                                Option: <strong>{resultMap[parseInt(bet.fields[3].value + "")]}</strong>
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-gray-400 text-xs">Tx ID:</p>
                                                            <p className="text-blue-500 text-xs truncate">
                                                                <a
                                                                    href={`${process.env.REACT_APP_EXPLORER_URL}/transactions/${transactionId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="hover:underline"
                                                                >
                                                                    {formatTxId(transactionId)}
                                                                </a>
                                                            </p>
                                                            <p className="text-gray-400 text-xs">Time:</p>
                                                            <p className="text-gray-300 text-xs">{timeOfAction}</p>
                                                        </div>
                                                    </li>
                                                );
                                            } else if (bet.eventIndex == 8) {
                                                return (
                                                    <li key={index} className="bg-green-800 p-4 rounded-lg shadow-md flex justify-between items-center">
                                                        <div>
                                                            <p className="text-gray-300">
                                                                Round: <strong>{bet.fields[1].value + ""}</strong>
                                                            </p>
                                                            <p className="text-gray-300">
                                                                Win Amount: <strong>{prettifyAttoAlphAmount(BigInt(bet.fields[2].value + ""))} ALPH</strong>
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-gray-400 text-xs">Tx ID:</p>
                                                            <p className="text-blue-500 text-xs truncate">
                                                                <a
                                                                    href={`${process.env.REACT_APP_EXPLORER_URL}/transactions/${transactionId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="hover:underline"
                                                                >
                                                                    {formatTxId(transactionId)}
                                                                </a>
                                                            </p>
                                                            <p className="text-gray-400 text-xs">Time:</p>
                                                            <p className="text-gray-300 text-xs">{timeOfAction}</p>
                                                        </div>
                                                    </li>
                                                );
                                            }
                                        }
                                        return null;
                                    })}
                                </ul>
                            )}
                        </div>
                    </>
                )
            }
        </div>
    );
}